import { Link } from "react-router-dom";
import {
    MessageSquareText,
    MapPin,
    Wrench,
    ShieldCheck,
    HeartHandshake,
} from "lucide-react";
import { SMS_HREF, SERVICE_AREA_LONG } from "@/lib/site";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/Breadcrumb";
import { useSiteText } from "@/lib/siteConfig";
import { EditableText } from "@/lib/editMode";

export default function About() {
    useSEO({
        title: "About — Ford-Trained Tech Building His Brand",
        description:
            "Limit Break Performance is run by a Ford dealership technician building his side brand — honest, mobile car work in Hampton Roads, Virginia. Read his story.",
    });

    const eyebrow = useSiteText("about.eyebrow", "The guy behind the wrench");
    const title = useSiteText("about.title", "Hey — I'm The Mechanic.");
    const paragraphs = useSiteText("about.paragraphs", [
        "I work professionally on cars every day at a Ford dealership here in Virginia. Limit Break Performance is the side I'm building on my own time — honest mobile work for people who don't want to deal with service advisors, markups, or up-sells.",
        "I grew up around cars, got trained on them professionally, and now I want to bring that experience straight to your driveway. No middleman. No corporate script. Just a technician who actually enjoys this stuff.",
    ]);
    const area = useSiteText("business.service_area_long", SERVICE_AREA_LONG);
    const credChip = useSiteText("about.credential_chip", "Ford dealership technician");
    const howIWorkTitle = useSiteText("about.how_title", "How I Work");
    const howIWorkBlocks = useSiteText("about.how_blocks", [
        { t: "Straight answers", d: "I'll tell you what the car actually needs. No padded invoices, no mystery fees." },
        { t: "Done properly", d: "Torque specs followed, fluids topped, test drive after. Like it's my own car." },
        { t: "Mobile first", d: "I bring the tools. You keep your day. Most jobs done in your driveway." },
    ]);
    const aboutImage = useSiteText(
        "about.image_url",
        "https://customer-assets.emergentagent.com/job_brake-performance-va/artifacts/erg2gqdo_IMG_7211.jpeg",
    );
    const blockIcons = [HeartHandshake, ShieldCheck, Wrench];

    return (
        <div data-testid="about-page">
            <Breadcrumb section="About" />
            <section className="border-b border-white/10 bg-black">
                <div className="lbp-container pt-8 pb-16 sm:pb-20 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <EditableText
                            path="about.eyebrow"
                            defaultValue={eyebrow}
                            as="p"
                            className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                        />
                        <EditableText
                            path="about.title"
                            defaultValue={title}
                            as="h1"
                            className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                            data-testid="about-hero-title"
                        />
                        {(Array.isArray(paragraphs) ? paragraphs : []).map((p, i) => (
                            <EditableText
                                key={i}
                                path={`about.paragraphs.${i}`}
                                defaultValue={p}
                                multiline
                                as="p"
                                className={`${i === 0 ? "mt-5 text-zinc-300 text-base sm:text-lg" : "mt-4 text-zinc-400"} leading-relaxed`}
                                data-testid={`about-paragraph-${i}`}
                            />
                        ))}

                        <div className="mt-7 flex flex-wrap gap-4 text-sm text-zinc-300">
                            <span className="inline-flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-red-500" />
                                {area}
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Wrench className="w-4 h-4 text-red-500" />
                                <EditableText
                                    path="about.credential_chip"
                                    defaultValue={credChip}
                                    as="span"
                                />
                            </span>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={SMS_HREF}
                                data-testid="about-text-button"
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                <MessageSquareText className="w-4 h-4" />
                                Text Me
                            </a>
                            <Link
                                to="/services"
                                data-testid="about-services-link"
                                className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-6 py-3.5 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                See Services
                            </Link>
                        </div>
                    </div>
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10">
                        <img
                            src={aboutImage}
                            alt="Engine pulled at a customer driveway"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    </div>
                </div>
            </section>

            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-14 sm:py-20">
                    <EditableText
                        path="about.how_title"
                        defaultValue={howIWorkTitle}
                        as="h2"
                        className="font-display font-black uppercase tracking-tight text-white text-3xl sm:text-4xl"
                    />
                    <div className="mt-10 grid sm:grid-cols-3 gap-4">
                        {(Array.isArray(howIWorkBlocks) ? howIWorkBlocks : []).map((b, i) => {
                            const Icon = blockIcons[i % blockIcons.length];
                            return (
                                <div
                                    key={b.t || i}
                                    className="rounded-sm border border-white/10 bg-zinc-900 p-6"
                                >
                                    <Icon className="w-6 h-6 text-red-500" />
                                    <EditableText
                                        path={`about.how_blocks.${i}.t`}
                                        defaultValue={b.t}
                                        as="h3"
                                        className="mt-5 font-display font-bold uppercase tracking-wide text-white text-lg"
                                    />
                                    <EditableText
                                        path={`about.how_blocks.${i}.d`}
                                        defaultValue={b.d}
                                        multiline
                                        as="p"
                                        className="mt-2 text-sm text-zinc-400 leading-relaxed"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}
